/**
 * modifierHelpers.ts — Utilidades para grupos de modificadores (restaurante / QSR).
 *
 * QUÉ HACE:
 *   - Construye claves estables de línea de carrito (`modifierKey`)
 *   - Valida selección min/max por grupo
 *   - Expone labels legibles para carrito y pedidos
 *
 * QUÉ EXPORTA: buildModifierKey, validateModifierSelection, etc.
 *
 * QUÉ NO HACE:
 *   - No persiste datos (el descuento de stock vive en modifierStock + handlers MSW)
 */
import type { ModifierGroup, ModifierOption } from "@/types";

const UNLIMITED_QTY = Number.MAX_SAFE_INTEGER;

export interface ModifierSelectedLabel {
  groupId: string;
  groupLabel: string;
  optionId: string;
  optionLabel: string;
  precioExtra?: number;
}

/** Texto libre guardado en selección (optionId especial). */
export const MODIFIER_TEXT_OPTION_ID = "__text__";

const TEXT_VALUE_PREFIX = "__text__:";

export const encodeModifierTextValue = (text: string): string =>
  `${TEXT_VALUE_PREFIX}${text}`;

export const decodeModifierTextValue = (stored: string): string | null =>
  stored.startsWith(TEXT_VALUE_PREFIX)
    ? stored.slice(TEXT_VALUE_PREFIX.length)
    : null;

export const getModifierGroupText = (
  grupo: ModifierGroup,
  seleccion: ModifierSelection,
): string => {
  if (grupo.tipoSeleccion !== "text") return "";
  const raw = seleccion[grupo.id]?.[0];
  if (!raw) return "";
  return decodeModifierTextValue(raw) ?? raw;
};

export const setModifierGroupText = (
  grupo: ModifierGroup,
  seleccion: ModifierSelection,
  text: string,
): ModifierSelection => {
  if (!text.trim()) {
    const next = { ...seleccion };
    delete next[grupo.id];
    return next;
  }
  return {
    ...seleccion,
    [grupo.id]: [encodeModifierTextValue(text)],
  };
};

/** Selección por grupo: optionIds o texto libre (tipo `text`). */
export type ModifierSelection = Record<string, string[]>;

/** Cuántas veces está elegida una opción dentro del grupo (permite duplicados). */
export const countOptionSelected = (
  selectedIds: string[],
  optionId: string,
): number => selectedIds.filter((id) => id === optionId).length;

/** Clave estable para distinguir líneas del mismo producto con distintos extras. */
export const buildModifierKey = (seleccion: ModifierSelection): string => {
  const parts = Object.keys(seleccion)
    .sort()
    .flatMap((groupId) => {
      const ids = seleccion[groupId] ?? [];
      if (ids.length === 0) return [];
      const counts = new Map<string, number>();
      for (const optionId of ids) {
        counts.set(optionId, (counts.get(optionId) ?? 0) + 1);
      }
      return [...counts.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([optionId, n]) =>
          n > 1 ? `${groupId}=${optionId}x${n}` : `${groupId}=${optionId}`,
        );
    });
  return parts.join("|");
};

export const countSelected = (
  seleccion: ModifierSelection,
  groupId: string,
): number => seleccion[groupId]?.length ?? 0;

export const getEffectiveMin = (grupo: ModifierGroup): number => {
  if (grupo.tipoSeleccion === "text") {
    return grupo.required ? 1 : (grupo.min ?? 0);
  }
  if (grupo.tipoSeleccion === "single" || grupo.tipoSeleccion === "boolean") {
    if (grupo.required || (grupo.min != null && grupo.min >= 1)) {
      return Math.max(1, grupo.min ?? 1);
    }
    return grupo.min ?? 0;
  }
  if (grupo.min != null) {
    if (grupo.required && grupo.min === 0) return 1;
    return grupo.min;
  }
  if (grupo.required) return 1;
  return 0;
};

export const isOpcionExclusiva = (
  grupo: ModifierGroup,
  optionId: string,
): boolean => !!findOption(grupo, optionId)?.exclusiva;

/** ID de opción exclusiva seleccionada en el grupo, si hay alguna. */
export const getExclusiveSelectedId = (
  grupo: ModifierGroup,
  selectedIds: string[],
): string | null =>
  selectedIds.find((id) => isOpcionExclusiva(grupo, id)) ?? null;

/** Atenúa fila (tope del grupo u opción exclusiva activa). */
export const isOptionDimmedBySelection = (
  grupo: ModifierGroup,
  selectedIds: string[],
  optionId: string,
  atMax: boolean,
  useQtyControls: boolean,
): boolean => {
  const qty = countOptionSelected(selectedIds, optionId);
  const isSelected = qty > 0;
  const exclusiveId = getExclusiveSelectedId(grupo, selectedIds);
  if (exclusiveId && optionId !== exclusiveId) return true;
  if (!useQtyControls && atMax && !isSelected) return true;
  if (useQtyControls && atMax && qty === 0) return true;
  return false;
};

/** Bloqueo de clic — en single/boolean no bloquea por exclusiva (permite cambiar). */
export const isOptionBlockedBySelection = (
  grupo: ModifierGroup,
  selectedIds: string[],
  optionId: string,
  atMax: boolean,
  useQtyControls: boolean,
): boolean => {
  const qty = countOptionSelected(selectedIds, optionId);
  const isSelected = qty > 0;
  const exclusiveId = getExclusiveSelectedId(grupo, selectedIds);
  if (
    exclusiveId &&
    optionId !== exclusiveId &&
    grupo.tipoSeleccion === "multiple"
  ) {
    return true;
  }
  if (!useQtyControls && atMax && !isSelected) return true;
  if (useQtyControls && atMax && qty === 0) return true;
  return false;
};

/** Grupos multiple con +/- por opción (extras repetibles). Default = opciones distintas. */
export const usesQuantityPerOption = (grupo: ModifierGroup): boolean =>
  grupo.tipoSeleccion === "multiple" && !!grupo.permiteCantidadPorOpcion;

export const getEffectiveMax = (grupo: ModifierGroup): number => {
  if (grupo.max != null) return grupo.max;
  if (grupo.tipoSeleccion === "single" || grupo.tipoSeleccion === "boolean")
    return 1;
  if (usesQuantityPerOption(grupo)) return UNLIMITED_QTY;
  return grupo.opciones.length;
};

export const optionHasStockLimit = (opt: ModifierOption): boolean =>
  opt.stock != null && opt.stock >= 0;

/** Unidades restantes de la opción (null = sin límite de inventario). */
export const getOptionStockRestante = (
  opt: ModifierOption,
  stockUsadoPorOption: Record<string, number> = {},
): number | null => {
  if (!optionHasStockLimit(opt)) return null;
  const used = stockUsadoPorOption[opt.id] ?? 0;
  return Math.max(0, (opt.stock ?? 0) - used);
};

export const getOptionMaxQty = (
  grupo: ModifierGroup,
  opt: ModifierOption,
  stockUsadoPorOption: Record<string, number> = {},
): number => {
  let max = 1;
  if (usesQuantityPerOption(grupo)) {
    if (opt.maxCantidad != null && opt.maxCantidad > 0) {
      max = opt.maxCantidad;
    } else if (grupo.max != null) {
      max = grupo.max;
    } else {
      max = UNLIMITED_QTY;
    }
  }
  const restante = getOptionStockRestante(opt, stockUsadoPorOption);
  if (restante != null) max = Math.min(max, restante);
  return max;
};

const getOptionMaxQtyInternal = getOptionMaxQty;

/** Texto guía visible en tienda/caja y en el editor admin. */
export const hintForModifierGroup = (grupo: ModifierGroup): string => {
  if (grupo.tipoSeleccion === "text") {
    if (grupo.required) return "Escribe un comentario";
    return "Opcional — comentario libre";
  }
  const min = getEffectiveMin(grupo);
  const max = getEffectiveMax(grupo);
  const count = grupo.opciones.length;

  if (grupo.tipoSeleccion === "single" || grupo.tipoSeleccion === "boolean") {
    if (grupo.required || min >= 1) return "Selecciona 1";
    return "Opcional — elige 1";
  }

  if (usesQuantityPerOption(grupo)) {
    if (grupo.max != null) return `Hasta ${max} unidades (puede repetir)`;
    return "Opcional — puede repetir con +/−";
  }

  if (min > 0 && max < count) return `Elige entre ${min} y ${max} distintas`;
  if (min > 0 && max >= count) return `Mínimo ${min}, hasta ${max} distintas`;
  if (min > 0) return `Mínimo ${min} distintas`;
  if (grupo.max != null && max < count) return `Máximo ${max} distintas`;
  if (grupo.max != null && max >= count) return `Hasta ${max} distintas`;
  return "Opcional";
};

/** Elimina selecciones inválidas tras editar grupos (preview / formulario). */
export const pruneModifierSelection = (
  grupos: ModifierGroup[],
  seleccion: ModifierSelection,
): ModifierSelection => {
  const out: ModifierSelection = {};
  for (const g of grupos) {
    if (g.tipoSeleccion === "text") {
      const text = getModifierGroupText(g, seleccion);
      if (text) out[g.id] = [encodeModifierTextValue(text)];
      continue;
    }
    const ids = seleccion[g.id] ?? [];
    const enabled = new Set(
      g.opciones.filter((o) => !o.disabled).map((o) => o.id),
    );
    const max = getEffectiveMax(g);
    let valid = ids.filter((id) => enabled.has(id));
    if (usesQuantityPerOption(g)) {
      valid = valid.slice(0, max);
    } else if (g.tipoSeleccion === "multiple") {
      const seen = new Set<string>();
      const distinct: string[] = [];
      for (const id of valid) {
        if (seen.has(id)) continue;
        seen.add(id);
        distinct.push(id);
        if (distinct.length >= max) break;
      }
      valid = distinct;
    } else {
      valid = valid.slice(0, max);
    }
    if (valid.length > 0) out[g.id] = valid;
  }
  return out;
};

/** ¿El grupo condicional debe mostrarse según la selección actual? */
export const isGrupoVisible = (
  grupo: ModifierGroup,
  seleccion: ModifierSelection,
): boolean => {
  if (!grupo.visibleCuando) return true;
  const { grupoId, optionIds } = grupo.visibleCuando;
  const selected = seleccion[grupoId] ?? [];
  return selected.some((id) => optionIds.includes(id));
};

/** Filtra grupos visibles según reglas condicionales. */
export const filterGruposVisibles = (
  grupos: ModifierGroup[],
  seleccion: ModifierSelection,
): ModifierGroup[] => grupos.filter((g) => isGrupoVisible(g, seleccion));

export interface ModifierValidationOptions {
  /** Tienda siempre estricta; en caja depende de `modificadoresValidacionCaja`. */
  canal?: "caja" | "tienda";
  modificadoresValidacionCaja?: "completa" | "libre";
}

/** ¿Exigir grupos obligatorios antes de agregar al carrito? */
export const enforceRequiredModifierGroups = (
  opts?: ModifierValidationOptions,
): boolean => {
  if (opts?.canal !== "caja") return true;
  return (opts.modificadoresValidacionCaja ?? "completa") !== "libre";
};

export const isGroupValid = (
  grupo: ModifierGroup,
  seleccion: ModifierSelection,
  stockUsadoPorOption: Record<string, number> = {},
  enforceRequired = true,
): boolean => {
  if (grupo.tipoSeleccion === "text") {
    const text = getModifierGroupText(grupo, seleccion);
    const min = enforceRequired ? getEffectiveMin(grupo) : 0;
    if (min >= 1 && !text.trim()) return false;
    return true;
  }
  const count = countSelected(seleccion, grupo.id);
  const min = enforceRequired ? getEffectiveMin(grupo) : 0;
  const max = getEffectiveMax(grupo);
  if (count < min || count > max) return false;

  for (const optionId of seleccion[grupo.id] ?? []) {
    const opt = findOption(grupo, optionId);
    if (!opt) return false;
    const restante = getOptionStockRestante(opt, stockUsadoPorOption);
    if (restante === 0) return false;
  }
  return true;
};

export const validateModifierSelection = (
  grupos: ModifierGroup[],
  seleccion: ModifierSelection,
  stockUsadoPorOption: Record<string, number> = {},
  validation?: ModifierValidationOptions,
): { valid: boolean; missingLabels: string[] } => {
  const enforceRequired = enforceRequiredModifierGroups(validation);
  const visible = filterGruposVisibles(grupos, seleccion);
  const missingLabels: string[] = [];
  for (const g of visible) {
    if (!isGroupValid(g, seleccion, stockUsadoPorOption, enforceRequired)) {
      missingLabels.push(g.label);
    }
  }
  return { valid: missingLabels.length === 0, missingLabels };
};

export const findOption = (
  grupo: ModifierGroup,
  optionId: string,
): ModifierOption | undefined => grupo.opciones.find((o) => o.id === optionId);

export const buildModifierLabels = (
  grupos: ModifierGroup[],
  seleccion: ModifierSelection,
): ModifierSelectedLabel[] => {
  const out: ModifierSelectedLabel[] = [];
  for (const g of grupos) {
    if (g.tipoSeleccion === "text") {
      const text = getModifierGroupText(g, seleccion).trim();
      if (text) {
        out.push({
          groupId: g.id,
          groupLabel: g.label,
          optionId: MODIFIER_TEXT_OPTION_ID,
          optionLabel: text,
        });
      }
      continue;
    }
    for (const optionId of seleccion[g.id] ?? []) {
      const opt = findOption(g, optionId);
      if (!opt) continue;
      out.push({
        groupId: g.id,
        groupLabel: g.label,
        optionId: opt.id,
        optionLabel: opt.label,
        precioExtra: opt.precioExtra,
      });
    }
  }
  return out;
};

/** Grupos cuyos ajustes de precio quedan en $0 por opciones con `anulaAjustesDeGrupos`. */
export const getGruposConAjustesAnulados = (
  grupos: ModifierGroup[],
  seleccion: ModifierSelection,
): Set<string> => {
  const anulados = new Set<string>();
  for (const g of grupos) {
    for (const optionId of seleccion[g.id] ?? []) {
      const opt = findOption(g, optionId);
      opt?.anulaAjustesDeGrupos?.forEach((grupoId) => anulados.add(grupoId));
    }
  }
  return anulados;
};

export const sumModifierExtras = (
  grupos: ModifierGroup[],
  seleccion: ModifierSelection,
): number => {
  const visible = filterGruposVisibles(grupos, seleccion);
  const anulados = getGruposConAjustesAnulados(grupos, seleccion);
  return buildModifierLabels(visible, seleccion).reduce((sum, l) => {
    if (anulados.has(l.groupId)) return sum;
    return sum + (l.precioExtra ?? 0);
  }, 0);
};

/** Quita selección de grupos ocultos (condicionales) y opciones inválidas. */
export const pruneHiddenModifierSelection = (
  grupos: ModifierGroup[],
  seleccion: ModifierSelection,
): ModifierSelection => {
  const visibleIds = new Set(
    filterGruposVisibles(grupos, seleccion).map((g) => g.id),
  );
  const trimmed: ModifierSelection = {};
  for (const g of grupos) {
    if (!visibleIds.has(g.id)) continue;
    const ids = seleccion[g.id];
    if (ids?.length) trimmed[g.id] = ids;
  }
  return pruneModifierSelection(grupos, trimmed);
};

/** Toggle opción respetando tipo y límites del grupo. */
/** Fija la selección de un grupo respetando opciones habilitadas y max. */
export const setModifierGroupSelection = (
  grupo: ModifierGroup,
  seleccion: ModifierSelection,
  optionIds: string[],
): ModifierSelection => {
  const enabled = new Set(
    grupo.opciones.filter((o) => !o.disabled).map((o) => o.id),
  );
  const max = getEffectiveMax(grupo);
  const next = optionIds.filter((id) => enabled.has(id)).slice(0, max);
  return { ...seleccion, [grupo.id]: next };
};

export const selectAllModifierOptions = (
  grupo: ModifierGroup,
  seleccion: ModifierSelection,
): ModifierSelection =>
  setModifierGroupSelection(
    grupo,
    seleccion,
    grupo.opciones.filter((o) => !o.disabled && !o.exclusiva).map((o) => o.id),
  );

export const clearModifierGroupSelection = (
  grupo: ModifierGroup,
  seleccion: ModifierSelection,
): ModifierSelection => setModifierGroupSelection(grupo, seleccion, []);

export const toggleModifierOption = (
  grupo: ModifierGroup,
  seleccion: ModifierSelection,
  optionId: string,
  stockUsadoPorOption: Record<string, number> = {},
): ModifierSelection => {
  const current = seleccion[grupo.id] ?? [];
  const isSelected = current.includes(optionId);
  const max = getEffectiveMax(grupo);
  const opt = findOption(grupo, optionId);

  if (grupo.tipoSeleccion === "single" || grupo.tipoSeleccion === "boolean") {
    if (isSelected) {
      return { ...seleccion, [grupo.id]: [] };
    }
    if (opt && getOptionStockRestante(opt, stockUsadoPorOption) === 0) {
      return seleccion;
    }
    return { ...seleccion, [grupo.id]: [optionId] };
  }

  if (usesQuantityPerOption(grupo)) {
    if (isSelected) {
      const idx = current.lastIndexOf(optionId);
      if (idx === -1) return seleccion;
      const next = [...current];
      next.splice(idx, 1);
      return { ...seleccion, [grupo.id]: next };
    }
    return incrementModifierOption(
      grupo,
      seleccion,
      optionId,
      stockUsadoPorOption,
    );
  }

  if (isSelected) {
    return {
      ...seleccion,
      [grupo.id]: current.filter((id) => id !== optionId),
    };
  }

  if (opt?.exclusiva) {
    if (getOptionStockRestante(opt, stockUsadoPorOption) === 0)
      return seleccion;
    return { ...seleccion, [grupo.id]: [optionId] };
  }

  const withoutExclusive = current.filter(
    (id) => !isOpcionExclusiva(grupo, id),
  );
  if (withoutExclusive.length >= max) return seleccion;
  if (opt && getOptionStockRestante(opt, stockUsadoPorOption) === 0) {
    return seleccion;
  }
  return { ...seleccion, [grupo.id]: [...withoutExclusive, optionId] };
};

/** Suma una unidad más de la opción (grupos multiple con cantidad). */
export const incrementModifierOption = (
  grupo: ModifierGroup,
  seleccion: ModifierSelection,
  optionId: string,
  stockUsadoPorOption: Record<string, number> = {},
): ModifierSelection => {
  if (grupo.tipoSeleccion !== "multiple") {
    return toggleModifierOption(
      grupo,
      seleccion,
      optionId,
      stockUsadoPorOption,
    );
  }
  if (!usesQuantityPerOption(grupo)) {
    return toggleModifierOption(
      grupo,
      seleccion,
      optionId,
      stockUsadoPorOption,
    );
  }
  const current = seleccion[grupo.id] ?? [];
  const max = getEffectiveMax(grupo);
  if (grupo.max != null && current.length >= max) return seleccion;
  const opt = findOption(grupo, optionId);
  if (!opt || opt.disabled) return seleccion;
  if (
    countOptionSelected(current, optionId) >=
    getOptionMaxQtyInternal(grupo, opt, stockUsadoPorOption)
  ) {
    return seleccion;
  }
  return { ...seleccion, [grupo.id]: [...current, optionId] };
};

/** Resta una unidad de la opción (grupos multiple). */
export const decrementModifierOption = (
  grupo: ModifierGroup,
  seleccion: ModifierSelection,
  optionId: string,
): ModifierSelection => {
  if (grupo.tipoSeleccion !== "multiple") {
    if (!(seleccion[grupo.id] ?? []).includes(optionId)) return seleccion;
    return { ...seleccion, [grupo.id]: [] };
  }
  const current = seleccion[grupo.id] ?? [];
  const idx = current.lastIndexOf(optionId);
  if (idx === -1) return seleccion;
  const next = [...current];
  next.splice(idx, 1);
  return { ...seleccion, [grupo.id]: next };
};

export const pickDefaultModifierSelection = (
  grupos: ModifierGroup[],
): ModifierSelection => {
  const out: ModifierSelection = {};
  for (const g of grupos) {
    const defaults = g.opciones.filter((o) => o.isDefault && !o.disabled);
    if (defaults.length > 0) {
      const max = getEffectiveMax(g);
      out[g.id] = defaults.slice(0, max).map((o) => o.id);
    }
  }
  return out;
};

export const newModifierGroupId = (): string =>
  `mod_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

export const newModifierOptionId = (): string =>
  `mop_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
